"use client"
import {
    Accordion,
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Input,
    Slider,
    Spinner,
    Table,
    Text,
    Tooltip,
    Portal,
} from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useCallback, useReducer, useRef, useState } from 'react';
import { useDcfWorkbenchData } from '../../hooks/useDcfWorkbenchData';
import { toast } from 'react-toastify';
import { useAuth0 } from '@auth0/auth0-react';
import { createRating, getRatingUsage } from '../../api';
import {
    ratingInitialState,
    ratingReducer,
    usageInitialState,
    usageReducer,
} from './stockAnalysis/reducers';
import { ensureScreenshotCaptureStyles } from './stockAnalysis/screenshotUtils';
import StockAnalysisRatingPanel from './stockAnalysis/StockAnalysisRatingPanel';
import { useDcfController } from './stockAnalysis/useDcfController';
import { useDcfCalculation } from './stockAnalysis/useDcfCalculation';
import DcfScenarios from './stockAnalysis/DcfScenarios';

ensureScreenshotCaptureStyles();

const StockAnalysisProjectionChart = lazy(() => import('./StockAnalysisProjectionChart'));

const loadingMessages = [
    'Reading the annual report...',
    'Checking the income statement...',
    'Analyzing free cash flow trends...',
    'Measuring the competitive moat...',
    'Evaluating management quality...',
    'Calculating intrinsic value...',
    'Channeling Warren Buffett...',
    'Looking for margin of safety...',
    'Reviewing the balance sheet...',
    'Almost there...',
];

function formatCompact(value, { currency = false, percent = false } = {}) {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    if (percent) {
        return `${(Number(value) * 100).toFixed(1)}%`;
    }
    return new Intl.NumberFormat('en-US', {
        notation: 'compact',
        maximumFractionDigits: 2,
        style: currency ? 'currency' : 'decimal',
        currency: currency ? 'USD' : undefined,
    }).format(Number(value));
}

function getFiniteNumber(...values) {
    for (const value of values) {
        if (value == null || value === '') continue;
        const number = Number(value);
        if (Number.isFinite(number)) return number;
    }
    return null;
}

function calculateHistoricalFreeCashFlow(entry) {
    const directFreeCashFlow = getFiniteNumber(entry.fcf, entry.free_cash_flow);
    if (directFreeCashFlow !== null) return directFreeCashFlow;

    const operatingCashFlow = getFiniteNumber(
        entry.ocf,
        entry.operating_cash_flow,
        entry.cash_flows_from_operations,
    );
    const capitalExpenditures = getFiniteNumber(
        entry.capex,
        entry.capital_expenditures,
        entry.capitalExpenditures,
    );

    if (operatingCashFlow === null) return null;
    return operatingCashFlow - (capitalExpenditures || 0);
}

function getHistoricalOperatingCashFlow(entry) {
    return getFiniteNumber(
        entry.ocf,
        entry.operating_cash_flow,
        entry.cash_flows_from_operations,
    );
}

function getHistoricalCapitalExpenditures(entry) {
    return getFiniteNumber(
        entry.capex,
        entry.capital_expenditures,
        entry.capitalExpenditures,
    );
}

function calculateForecastOperatingCashFlow(entry) {
    const fcf = getFiniteNumber(entry.fcff);
    const capex = getFiniteNumber(entry.capex);
    return fcf !== null && capex !== null ? fcf + capex : null;
}

function calculateGrowthRate(currentValue, priorValue) {
    const current = getFiniteNumber(currentValue);
    const prior = getFiniteNumber(priorValue);
    return current !== null && prior !== null && prior !== 0 ? current / prior - 1 : null;
}

function SectionCard({ id, title, description, action, children }) {
    return (
        <Box
            as="section"
            id={id}
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            bg="white"
            boxShadow="sm"
            p={{ base: 4, md: 6 }}
        >
            <Flex
                align={{ base: 'flex-start', md: 'center' }}
                justify="space-between"
                gap={4}
                direction={{ base: 'column', md: 'row' }}
                mb={description ? 5 : 0}
            >
                <Box>
                    <Heading as="h2" size="lg">{title}</Heading>
                    {description && (
                        <Text color="gray.600" mt={1}>{description}</Text>
                    )}
                </Box>
                {action}
            </Flex>
            {children}
        </Box>
    );
}

function parseAbbreviatedNumber(value) {
    const normalized = String(value ?? '').trim().replace(/[$,\s]/g, '');
    const match = normalized.match(/^(-?(?:\d+\.?\d*|\.\d+))([kmbt])?$/i);
    if (!match) return null;
    const multipliers = { k: 1e3, m: 1e6, b: 1e9, t: 1e12 };
    const parsed = Number(match[1]) * (multipliers[match[2]?.toLowerCase()] || 1);
    return Number.isFinite(parsed) ? parsed : null;
}

function AssumptionInput({
    label,
    ariaLabel,
    value,
    field,
    dispatch,
    percent = false,
    abbreviated = false,
    currency = false,
    min,
    max,
    step,
    tooltip,
}) {
    const formattedValue = value == null
        ? ''
        : percent
            ? Number((value * 100).toFixed(2))
            : abbreviated
                ? formatCompact(value, { currency })
                : value;
    const [draftValue, setDraftValue] = useState(String(formattedValue));
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            setDraftValue(String(formattedValue));
        }
    }, [formattedValue, isFocused]);

    const parseRawValue = (rawValue) => {
        const text = String(rawValue).trim();
        if (text === '' || text === '-' || text === '.' || text === '-.') return null;
        return abbreviated ? parseAbbreviatedNumber(text) : Number(text);
    };

    const commitValue = (rawValue) => {
        const parsed = parseRawValue(rawValue);
        if (parsed == null || !Number.isFinite(parsed)) return;
        dispatch({
            type: 'set_field',
            field,
            value: percent ? parsed / 100 : parsed,
        });
    };

    return (
        <Box>
            <Flex align="center" gap={1} mb={2}>
                <Text fontWeight="semibold">{label}</Text>
                {tooltip}
            </Flex>
            <Input
                aria-label={ariaLabel || label}
                value={isFocused ? draftValue : String(formattedValue)}
                onChange={(event) => {
                    setDraftValue(event.target.value);
                    commitValue(event.target.value);
                }}
                onFocus={() => {
                    setIsFocused(true);
                    setDraftValue(value == null ? '' : percent ? String(Number((value * 100).toFixed(2))) : String(value));
                }}
                onBlur={() => {
                    setIsFocused(false);
                    setDraftValue(String(formattedValue));
                }}
                type="text"
                inputMode={abbreviated ? 'text' : 'decimal'}
                min={min}
                max={max}
                step={step}
            />
            {percent && (
                <Slider.Root
                    mt={3}
                    value={[Number.isFinite(Number(formattedValue)) ? Number(formattedValue) : 0]}
                    min={min}
                    max={max}
                    step={step}
                    onValueChange={(event) => commitValue(event.value[0])}
                >
                    <Slider.Control>
                        <Slider.Track bg="gray.200">
                            <Slider.Range bg="green.500" />
                        </Slider.Track>
                        <Slider.Thumb index={0} />
                    </Slider.Control>
                </Slider.Root>
            )}
        </Box>
    );
}

function average(values) {
    const finiteValues = values.filter((value) => Number.isFinite(value));
    return finiteValues.length
        ? finiteValues.reduce((sum, value) => sum + value, 0) / finiteValues.length
        : null;
}

function AverageButtons({ driver, history, dispatch }) {
    return (
        <Flex gap={2} wrap="wrap" mt={3}>
            {[3, 5, 10].map((years) => {
                const value = average(history.slice(-years).map((entry) => entry[driver]));
                return (
                    <Button
                        key={years}
                        size="xs"
                        variant="outline"
                        disabled={value == null}
                        onClick={() => dispatch({
                            type: 'apply_average',
                            driver,
                            value,
                        })}
                    >
                        Apply avg {years}
                    </Button>
                );
            })}
        </Flex>
    );
}

function ValuationBridge({ result, loading, error, currentPrice, sharesOutstanding, dispatch }) {
    const sharesInput = (
        <Box mb={5} maxW={{ base: '100%', md: '320px' }}>
            <AssumptionInput
                label="Shares Outstanding"
                ariaLabel="Valuation Shares Outstanding"
                value={sharesOutstanding}
                field="sharesOutstanding"
                dispatch={dispatch}
                abbreviated
            />
        </Box>
    );

    if (loading && !result) {
        return (
            <Box mt={5}>
                {sharesInput}
                <Flex justify="center" py={8}><Spinner color="green.500" /></Flex>
            </Box>
        );
    }
    if (error) {
        return (
            <Box mt={5}>
                {sharesInput}
                <Text color="red.600">Unable to calculate valuation: {error}</Text>
            </Box>
        );
    }
    if (!result) {
        return (
            <Box mt={5}>
                {sharesInput}
                <Text color="gray.600">Enter complete assumptions to calculate the valuation.</Text>
            </Box>
        );
    }

    const price = Number(currentPrice);
    const intrinsicValue = Number(result.intrinsic_value_per_share);
    const upside = Number.isFinite(price) && price > 0
        ? intrinsicValue / price - 1
        : null;
    const rows = [
        ['PV of Forecast FCF', result.pv_forecast_fcff],
        ['PV of Terminal Value', result.terminal_value?.present_value],
        ['Enterprise Value', result.enterprise_value],
        ['− Net Debt', result.net_debt],
        ['Equity Value', result.equity_value],
    ];
    const sensitivityCells = result.sensitivity?.cells || [];
    const sensitivityWaccs = [...new Set(sensitivityCells.map((cell) => cell.wacc))];
    const sensitivityGrowthRates = [
        ...new Set(sensitivityCells.map((cell) => cell.terminal_growth)),
    ];

    return (
        <Box mt={5}>
            {loading && <Text color="gray.600" mb={3}>Recalculating…</Text>}
            {sharesInput}
            <Flex direction="column" gap={2}>
                {rows.map(([label, value]) => (
                    <Flex
                        key={label}
                        justify="space-between"
                        gap={4}
                        borderBottomWidth="1px"
                        borderColor="gray.100"
                        py={2}
                    >
                        <Text color="gray.600">{label}</Text>
                        <Text fontWeight="semibold">{formatCompact(value, { currency: true })}</Text>
                    </Flex>
                ))}
            </Flex>
            <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={3} mt={5}>
                <Box bg="green.50" borderRadius="lg" p={4}>
                    <Text fontSize="sm" color="gray.600">Intrinsic Value per Share</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="green.700">
                        {formatCompact(intrinsicValue, { currency: true })}
                    </Text>
                </Box>
                <Box bg={upside != null && upside < 0 ? 'red.50' : 'green.50'} borderRadius="lg" p={4}>
                    <Text fontSize="sm" color="gray.600">Upside / Downside vs Current Price</Text>
                    <Text
                        fontSize="2xl"
                        fontWeight="bold"
                        color={upside != null && upside < 0 ? 'red.700' : 'green.700'}
                    >
                        {formatCompact(upside, { percent: true })}
                    </Text>
                </Box>
            </Grid>
            {sensitivityCells.length > 0 && (
                <Box mt={7}>
                    <Heading as="h3" size="md" mb={1}>WACC × Terminal Growth Sensitivity</Heading>
                    <Text color="gray.600" fontSize="sm" mb={3}>
                        Intrinsic value per share across discount-rate and terminal-growth assumptions.
                    </Text>
                    <Box overflowX="auto">
                        <Box
                            as="table"
                            borderCollapse="collapse"
                            minW="560px"
                            width="100%"
                            aria-label="WACC by terminal growth sensitivity"
                        >
                            <Box as="thead">
                                <Box as="tr">
                                    <Box as="th" p={2} textAlign="left">WACC ↓ / Growth →</Box>
                                    {sensitivityGrowthRates.map((growth) => (
                                        <Box as="th" key={growth} p={2} textAlign="right">
                                            {(growth * 100).toFixed(1)}%
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                            <Box as="tbody">
                                {sensitivityWaccs.map((wacc) => (
                                    <Box as="tr" key={wacc}>
                                        <Box as="th" p={2} textAlign="left">
                                            {(wacc * 100).toFixed(1)}%
                                        </Box>
                                        {sensitivityGrowthRates.map((growth) => {
                                            const cell = sensitivityCells.find(
                                                (candidate) => candidate.wacc === wacc
                                                    && candidate.terminal_growth === growth,
                                            );
                                            return (
                                                <Box
                                                    as="td"
                                                    key={growth}
                                                    p={2}
                                                    textAlign="right"
                                                    bg={cell?.is_current ? 'green.100' : undefined}
                                                    borderWidth={cell?.is_current ? '2px' : '1px'}
                                                    borderColor={cell?.is_current ? 'green.600' : 'gray.200'}
                                                    aria-label={cell?.is_current ? 'Current assumptions' : undefined}
                                                >
                                                    {cell?.intrinsic_value_per_share == null
                                                        ? 'Invalid'
                                                        : formatCompact(
                                                            cell.intrinsic_value_per_share,
                                                            { currency: true },
                                                        )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    );
}

function CalculatedTableValue({ value, title, rows, resultLabel = title }) {
    const visibleRows = rows.filter((row) => row.value != null && Number.isFinite(Number(row.value)));
    if (!visibleRows.length || !Number.isFinite(Number(value))) {
        return formatCompact(value, { currency: true });
    }

    return (
        <Tooltip.Root openDelay={100} closeDelay={150} interactive>
            <Tooltip.Trigger asChild>
                <Text
                    as="span"
                    tabIndex={0}
                    cursor="help"
                    textDecorationLine="underline"
                    textDecorationStyle="dashed"
                    textUnderlineOffset="3px"
                    aria-label={`${title} calculation`}
                    data-calculated-value="true"
                >
                    {formatCompact(value, { currency: true })}
                </Text>
            </Tooltip.Trigger>
            <Portal>
                <Tooltip.Positioner zIndex="tooltip">
                    <Tooltip.Content
                        bg="white"
                        color="gray.800"
                        p={3}
                        borderRadius="md"
                        boxShadow="lg"
                        border="1px solid"
                        borderColor="gray.200"
                        minW="280px"
                    >
                        <Text fontWeight="bold" mb={2} fontSize="sm" textAlign="center">
                            {title}
                        </Text>
                        <Box fontSize="sm" fontFamily="mono">
                            {visibleRows.map((row) => (
                                <Flex key={row.label} justifyContent="space-between" alignItems="flex-end" mb={1}>
                                    <Text color="gray.700" mr={2} lineHeight="1.2">
                                        {row.operation === 'subtract' ? 'Less: ' : row.operation === 'add' ? 'Add: ' : ''}
                                        {row.label}
                                    </Text>
                                    <Box flex="1" borderBottom="1px dotted" borderColor="gray.400" mx={1} mb="0.15em" />
                                    <Text color="gray.800" textAlign="right" whiteSpace="nowrap" lineHeight="1.2">
                                        {row.operation === 'subtract' ? '(' : ''}
                                        {formatCompact(row.value, { currency: true })}
                                        {row.operation === 'subtract' ? ')' : ''}
                                    </Text>
                                </Flex>
                            ))}
                            <Flex justifyContent="space-between" alignItems="flex-end" mt={2} pt={1} borderTop="1px solid" borderColor="gray.400">
                                <Text fontWeight="bold" color="gray.700" mr={2} lineHeight="1.2">
                                    {resultLabel}
                                </Text>
                                <Box flex="1" borderBottom="1px dotted" borderColor="gray.400" mx={1} mb="0.15em" />
                                <Text fontWeight="bold" color="gray.900" textAlign="right" whiteSpace="nowrap" lineHeight="1.2">
                                    {formatCompact(value, { currency: true })}
                                </Text>
                            </Flex>
                        </Box>
                    </Tooltip.Content>
                </Tooltip.Positioner>
            </Portal>
        </Tooltip.Root>
    );
}

function DcfModelTable({
    result,
    loading,
    error,
    baseRevenue,
    history = [],
    onTerminalValueHighlightChange,
    onTerminalPvHighlightChange,
}) {
    if (loading && !result) {
        return <Flex justify="center" py={8}><Spinner color="green.500" /></Flex>;
    }
    if (error) {
        return <Text color="red.600">Unable to calculate model: {error}</Text>;
    }

    const years = result?.rows || [];
    if (years.length === 0) {
        return <Text color="gray.600">Enter complete assumptions to build the model.</Text>;
    }

    const historicalYears = history
        .filter((entry) => Number.isFinite(Number(entry.year)))
        .map((entry) => ({
            key: `historical-${entry.year}`,
            label: String(entry.year),
            phase: 'historical',
            data: entry,
        }));
    const forecastYears = years.map((entry) => ({
        key: `forecast-${entry.year}`,
        label: `Year ${entry.year}`,
        phase: 'forecast',
        data: entry,
    }));
    const columns = [...historicalYears, ...forecastYears];

    const firstForecastYear = years[0]?.year;
    const forecastDividerStyles = {
        borderLeftWidth: '2px',
        borderLeftColor: 'green.500',
    };

    const rowDefinitions = [
        {
            label: 'Revenue',
            forecast: (year) => formatCompact(year.revenue, { currency: true }),
            historical: (year) => formatCompact(year.revenue, { currency: true }),
        },
        {
            label: 'Revenue Growth %',
            forecast: (year, index) => {
                const priorRevenue = index === 0 ? Number(baseRevenue) : Number(years[index - 1].revenue);
                const growth = priorRevenue !== 0 ? Number(year.revenue) / priorRevenue - 1 : null;
                return formatCompact(growth, { percent: true });
            },
            historical: (year) => formatCompact(year.revenue_growth, { percent: true }),
        },
        {
            label: 'OCF',
            forecast: (year) => formatCompact(calculateForecastOperatingCashFlow(year), { currency: true }),
            historical: (year) => formatCompact(getHistoricalOperatingCashFlow(year), { currency: true }),
        },
        {
            label: '− Capex',
            forecast: (year) => formatCompact(year.capex, { currency: true }),
            historical: (year) => formatCompact(getHistoricalCapitalExpenditures(year), { currency: true }),
        },
        {
            label: 'FCF',
            forecast: (year) => {
                const operatingCashFlow = calculateForecastOperatingCashFlow(year);
                return (
                    <CalculatedTableValue
                        value={year.fcff}
                        title="Free cash flow"
                        resultLabel="FCF"
                        rows={[
                            { label: 'Operating Cash Flow', value: operatingCashFlow },
                            { label: 'Capital Expenditures', value: year.capex, operation: 'subtract' },
                        ]}
                    />
                );
            },
            historical: (year) => {
                const operatingCashFlow = getHistoricalOperatingCashFlow(year);
                const capitalExpenditures = getHistoricalCapitalExpenditures(year);

                return (
                    <CalculatedTableValue
                        value={year.fcf}
                        title="Free cash flow"
                        resultLabel="FCF"
                        rows={[
                            { label: 'Operating Cash Flow', value: operatingCashFlow },
                            { label: 'Capital Expenditures', value: capitalExpenditures ?? 0, operation: 'subtract' },
                        ]}
                    />
                );
            },
        },
        {
            label: 'FCF Growth %',
            forecast: (year, index) => {
                const priorFcf = index === 0
                    ? historicalYears[historicalYears.length - 1]?.data?.fcf
                    : years[index - 1]?.fcff;
                return formatCompact(calculateGrowthRate(year.fcff, priorFcf), { percent: true });
            },
            historical: (year, index) => {
                const priorFcf = historicalYears[index - 1]?.data?.fcf;
                return formatCompact(calculateGrowthRate(year.fcf, priorFcf), { percent: true });
            },
        },
        {
            label: 'PV of FCF',
            forecast: (year) => formatCompact(year.pv_fcff, { currency: true }),
            historical: () => '—',
        },
    ];

    return (
        <Box mt={5}>
            {loading && <Text color="gray.600" mb={3}>Recalculating…</Text>}
            <Box overflowX="auto">
                <Table.Root
                    size="sm"
                    minW={`${Math.max(720, columns.length * 120 + 160)}px`}
                    variant="line"
                    aria-label="DCF model historical and projected line items"
                >
                    <Table.Header>
                        <Table.Row>
                            <Table.ColumnHeader position="sticky" left={0} bg="white" zIndex={1}>Year</Table.ColumnHeader>
                            {columns.map((column) => (
                                <Table.ColumnHeader
                                    key={column.key}
                                    textAlign="right"
                                    color={column.phase === 'historical' ? 'gray.600' : undefined}
                                    data-projection-boundary={
                                        column.phase === 'forecast' && column.data.year === firstForecastYear
                                            ? 'true'
                                            : undefined
                                    }
                                    {...(column.phase === 'forecast' && column.data.year === firstForecastYear ? forecastDividerStyles : {})}
                                >
                                    {column.label}
                                </Table.ColumnHeader>
                            ))}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {rowDefinitions.map((row) => (
                            <Table.Row key={row.label}>
                                <Table.ColumnHeader
                                    scope="row"
                                    fontWeight={row.label === 'FCF' ? 'bold' : 'semibold'}
                                    whiteSpace="nowrap"
                                    position="sticky"
                                    left={0}
                                    bg="white"
                                    zIndex={1}
                                >
                                    {row.label}
                                </Table.ColumnHeader>
                                {columns.map((column, index) => {
                                    const forecastIndex = column.phase === 'forecast'
                                        ? years.findIndex((year) => year.year === column.data.year)
                                        : -1;
                                    return (
                                        <Table.Cell
                                            key={column.key}
                                            textAlign="right"
                                            color={column.phase === 'historical' ? 'gray.700' : undefined}
                                            fontWeight={row.label === 'FCF' ? 'bold' : 'normal'}
                                            data-projection-boundary={
                                                column.phase === 'forecast' && column.data.year === firstForecastYear
                                                    ? 'true'
                                                    : undefined
                                            }
                                            {...(column.phase === 'forecast' && column.data.year === firstForecastYear ? forecastDividerStyles : {})}
                                        >
                                            {column.phase === 'historical'
                                                ? row.historical(column.data, index)
                                                : row.forecast(column.data, forecastIndex)}
                                        </Table.Cell>
                                    );
                                })}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table.Root>
            </Box>
            <Box bg="gray.50" borderRadius="lg" p={4} mt={4}>
                <Text fontWeight="bold">Terminal Value (after Year {years.at(-1).year})</Text>
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)' }} gap={3} mt={2}>
                    <Box
                        tabIndex={0}
                        borderRadius="md"
                        p={2}
                        m={-2}
                        cursor="default"
                        transition="background-color 120ms ease"
                        _hover={{ bg: 'purple.50' }}
                        _focusVisible={{ outline: '2px solid', outlineColor: 'purple.400' }}
                        data-testid="terminal-value-source"
                        onMouseEnter={() => onTerminalValueHighlightChange?.(true)}
                        onMouseLeave={() => onTerminalValueHighlightChange?.(false)}
                        onFocus={() => onTerminalValueHighlightChange?.(true)}
                        onBlur={() => onTerminalValueHighlightChange?.(false)}
                    >
                        <Text fontSize="sm" color="gray.600">Terminal Value</Text>
                        <Text fontWeight="semibold">{formatCompact(result.terminal_value?.value, { currency: true })}</Text>
                    </Box>
                    <Box
                        tabIndex={0}
                        borderRadius="md"
                        p={2}
                        m={-2}
                        cursor="default"
                        transition="background-color 120ms ease"
                        _hover={{ bg: 'purple.50' }}
                        _focusVisible={{ outline: '2px solid', outlineColor: 'purple.300' }}
                        data-testid="terminal-pv-source"
                        onMouseEnter={() => onTerminalPvHighlightChange?.(true)}
                        onMouseLeave={() => onTerminalPvHighlightChange?.(false)}
                        onFocus={() => onTerminalPvHighlightChange?.(true)}
                        onBlur={() => onTerminalPvHighlightChange?.(false)}
                    >
                        <Text fontSize="sm" color="gray.600">Present Value of Terminal Value</Text>
                        <Text fontWeight="semibold">{formatCompact(result.terminal_value?.present_value, { currency: true })}</Text>
                    </Box>
                </Grid>
            </Box>
        </Box>
    );
}

function ModelAssumptionControls({ state, dispatch }) {
    return (
        <Grid
            templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(5, 1fr)' }}
            gap={5}
            mb={6}
        >
            <AssumptionInput
                label="Starting FCF"
                value={state.startingFcf}
                field="startingFcf"
                dispatch={dispatch}
                abbreviated
                currency
            />
            <AssumptionInput
                label="Forecast Years"
                value={state.forecastYears}
                field="forecastYears"
                dispatch={dispatch}
                min={1}
                max={30}
                step={1}
            />
            <AssumptionInput
                label="Discount Rate"
                value={state.wacc}
                field="wacc"
                dispatch={dispatch}
                percent
                min={1}
                max={30}
                step={0.25}
            />
            <AssumptionInput
                label="FCF Growth Rate"
                value={state.fcfGrowthRate}
                field="fcfGrowthRate"
                dispatch={dispatch}
                percent
                min={-20}
                max={50}
                step={0.5}
            />
            <AssumptionInput
                label="Terminal Growth Rate"
                value={state.terminalGrowth}
                field="terminalGrowth"
                dispatch={dispatch}
                percent
                min={-5}
                max={10}
                step={0.25}
            />
        </Grid>
    );
}

function ProjectionChart({ history, result, assumptions, highlightTerminal, highlightTerminalPv }) {
    const historicalPoints = history
        .filter((entry) => Number.isFinite(Number(entry.fcf)))
        .map((entry) => ({
            label: String(entry.year),
            historicalFcf: entry.fcf,
            projectedFcff: null,
            projectedFcfPv: null,
            terminalFcf: null,
            terminalPv: null,
            section: 'historical',
        }));
    const projectedPoints = (result?.rows || [])
        .filter((entry) => Number.isFinite(Number(entry.fcff)) || Number.isFinite(Number(entry.pv_fcff)))
        .map((entry) => ({
            label: `Year ${entry.year}`,
            historicalFcf: null,
            projectedFcff: entry.fcff,
            projectedFcfPv: entry.pv_fcff,
            terminalFcf: null,
            terminalPv: null,
            section: 'projection',
        }));
    const finalProjection = result?.rows?.at(-1);
    const forecastYears = Number(assumptions?.forecastYears);
    const terminalGrowth = Number(assumptions?.terminalGrowth);
    const wacc = Number(assumptions?.wacc);
    const terminalPoints = [];
    if (
        finalProjection
        && Number.isFinite(Number(finalProjection.fcff))
        && Number.isFinite(forecastYears)
        && Number.isFinite(terminalGrowth)
        && Number.isFinite(wacc)
        && wacc > terminalGrowth
    ) {
        terminalPoints.push({
            label: `Year ${finalProjection.year}`,
            historicalFcf: null,
            projectedFcff: null,
            projectedFcfPv: null,
            terminalFcf: finalProjection.fcff,
            terminalPv: finalProjection.pv_fcff,
            section: 'terminal-bridge',
        });
        for (let year = 1; year <= 20; year += 1) {
            const fcf = finalProjection.fcff * ((1 + terminalGrowth) ** year);
            terminalPoints.push({
                label: `Terminal ${year}`,
                historicalFcf: null,
                projectedFcff: null,
                projectedFcfPv: null,
                terminalFcf: fcf,
                terminalPv: fcf / ((1 + wacc) ** (forecastYears + year)),
                section: 'terminal',
            });
        }
    }

    if (historicalPoints.length === 0 || projectedPoints.length === 0) return null;

    return (
        <Suspense fallback={<Flex justify="center" py={8}><Spinner color="green.500" /></Flex>}>
            <StockAnalysisProjectionChart
                series={[...historicalPoints, ...projectedPoints, ...terminalPoints]}
                projectionStart={projectedPoints[0].label}
                terminalStart={terminalPoints.find((entry) => entry.section === 'terminal')?.label}
                terminalEnd={terminalPoints.at(-1)?.section === 'terminal' ? terminalPoints.at(-1).label : null}
                highlightTerminal={highlightTerminal}
                highlightTerminalPv={highlightTerminalPv}
            />
        </Suspense>
    );
}

function StockAnalysis() {
    const { ticker } = useParams();
    const normalizedTicker = ticker ? ticker.toUpperCase() : '';
    const [highlightTerminal, setHighlightTerminal] = useState(false);
    const [highlightTerminalPv, setHighlightTerminalPv] = useState(false);
    const {
        loading,
        error,
        driverHistory,
        defaults,
        baseRevenue,
        netDebt,
        shares,
        price,
        marketCap,
    } = useDcfWorkbenchData(ticker);
    const { state: dcfState, dispatch: dispatchDcf } = useDcfController({
        defaults,
        baseRevenue,
        netDebt,
        shares,
    });
    const {
        result: dcfResult,
        loading: isDcfLoading,
        error: dcfError,
    } = useDcfCalculation(dcfState);
    const {
        isAuthenticated,
        isLoading: isAuthLoading,
        getAccessTokenSilently,
        loginWithRedirect,
    } = useAuth0();
    const [
        {
            result: ratingResult,
            isLoading: isRatingLoading,
            error: ratingError,
            loadingMessageIndex,
            loadingProgress,
        },
        dispatchRating,
    ] = useReducer(ratingReducer, ratingInitialState);
    const [{ data: usage, isLoading: isUsageLoading, error: usageError }, dispatchUsage] = useReducer(
        usageReducer,
        usageInitialState
    );
    const loadingProgressRef = useRef(loadingProgress);

    useEffect(() => {
        loadingProgressRef.current = loadingProgress;
    }, [loadingProgress]);

    useEffect(() => {
        if (!isRatingLoading) return undefined;

        const messageInterval = setInterval(() => {
            dispatchRating({ type: 'tick_message', messageCount: loadingMessages.length });
        }, 2000);

        const progressInterval = setInterval(() => {
            dispatchRating({ type: 'tick_progress' });
        }, 800);

        return () => {
            clearInterval(messageInterval);
            clearInterval(progressInterval);
        };
    }, [isRatingLoading]);

    useEffect(() => {
        if (isRatingLoading || loadingProgressRef.current <= 0) return undefined;

        dispatchRating({ type: 'complete_progress' });
        const resetTimeout = setTimeout(() => {
            dispatchRating({ type: 'reset_progress' });
        }, 500);

        return () => clearTimeout(resetTimeout);
    }, [isRatingLoading]);

    const handleGenerateRating = async () => {
        if (!normalizedTicker) return;
        dispatchRating({ type: 'start' });
        try {
            const token = await getAccessTokenSilently();
            const response = await createRating(normalizedTicker, token);
            if (!response?.success || !response?.rating) {
                throw new Error('Could not generate rating.');
            }
            dispatchRating({ type: 'success', payload: response.rating });
            toast.success('AI rating generated!');
            await refreshUsage();
        } catch (err) {
            console.error('Failed to generate rating', err);
            const message = err?.response?.data?.message || err.message || 'Unable to generate rating.';
            dispatchRating({ type: 'error', payload: message });
            toast.error('Unable to generate AI rating at the moment.');
        }
    };

    const history = driverHistory
        .filter((entry) => entry.year <= new Date().getFullYear())
        .sort((a, b) => a.year - b.year)
        .slice(-10)
        .map((entry, index, entries) => {
            const previousRevenue = entries[index - 1]?.revenue;
            const fcf = calculateHistoricalFreeCashFlow(entry);
            return {
                ...entry,
                fcf,
                revenue_growth: previousRevenue != null && previousRevenue !== 0 && entry.revenue != null
                    ? entry.revenue / previousRevenue - 1
                    : null,
                fcf_margin: entry.revenue != null && entry.revenue !== 0 && fcf != null
                    ? fcf / entry.revenue
                    : null,
            };
        });

    useEffect(() => {
        const latestFcf = [...history]
            .reverse()
            .find((entry) => Number.isFinite(Number(entry.fcf)))
            ?.fcf;
        if (latestFcf == null) return;
        dispatchDcf({ type: 'set_starting_fcf_default', value: latestFcf });
    }, [history, dispatchDcf]);

    const refreshUsage = useCallback(async () => {
        if (!isAuthenticated) {
            dispatchUsage({ type: 'clear' });
            return;
        }
        dispatchUsage({ type: 'load_start' });
        try {
            const token = await getAccessTokenSilently();
            const data = await getRatingUsage(token);
            dispatchUsage({ type: 'load_success', payload: data });
        } catch (err) {
            console.error('Failed to load rating usage', err);
            dispatchUsage({ type: 'load_error', payload: 'Unable to load rating usage right now.' });
        }
    }, [getAccessTokenSilently, isAuthenticated]);

    useEffect(() => {
        if (!isAuthLoading) {
            refreshUsage();
        }
    }, [refreshUsage, isAuthLoading]);

    const isPremiumMember = usage?.is_premium === true;
    const usedRatings = usage?.used_this_month ?? 0;
    const monthlyLimit = isPremiumMember ? null : usage?.monthly_limit ?? 3;
    const canGenerateRating = usage?.can_generate !== false;
    const renewalDate = usage?.subscription_renewal_date ?? null;
    const renewalLabel = renewalDate
        ? new Date(renewalDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
        : null;
    const usageLabel = usage
        ? isPremiumMember
            ? `Premium member — unlimited ratings${renewalLabel ? ` • Renews ${renewalLabel}` : ''}`
            : `${usedRatings}/${monthlyLimit} ratings used this month`
        : isUsageLoading
            ? 'Checking usage...'
            : 'Usage data unavailable';
    const ratingButtonDisabled = isRatingLoading || (usage && usage.can_generate === false);
    const reverseDcfPath = `/stock/${String(ticker || '').toLowerCase()}/reverse-dcf`;

    return (
        <Flex direction="column" gap={6} w="100%" pb={8} pr={{ base: 0, lg: 2 }}>
            <SectionCard
                id="model"
                title="Model"
                description="Follow the forecast from revenue through free cash flow to the firm."
                action={(
                    <Button as={RouterLink} to={reverseDcfPath} variant="outline" colorPalette="green" alignSelf={{ base: 'flex-end', md: 'center' }}>
                        Open Reverse DCF
                    </Button>
                )}
            >
                <ModelAssumptionControls state={dcfState} dispatch={dispatchDcf} />
                <ProjectionChart
                    history={history}
                    result={dcfResult}
                    assumptions={dcfState}
                    highlightTerminal={highlightTerminal}
                    highlightTerminalPv={highlightTerminalPv}
                />
                <DcfModelTable
                    result={dcfResult}
                    loading={isDcfLoading}
                    error={dcfError}
                    baseRevenue={dcfState.baseRevenue}
                    history={history}
                    onTerminalValueHighlightChange={setHighlightTerminal}
                    onTerminalPvHighlightChange={setHighlightTerminalPv}
                />
            </SectionCard>

            <SectionCard
                id="understand"
                title={`Understand ${normalizedTicker}`}
                description="Start with the current market context and the historical drivers behind the business."
            >
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(3, 1fr)' }} gap={3} mb={6}>
                    {[
                        ['Current Price', formatCompact(price?.value, { currency: true })],
                        ['Market Cap', formatCompact(marketCap, { currency: true })],
                        ['Shares Outstanding', formatCompact(shares?.value)],
                    ].map(([label, value]) => (
                        <Box key={label} bg="gray.50" borderRadius="lg" p={4}>
                            <Text fontSize="sm" color="gray.600">{label}</Text>
                            <Text fontSize="xl" fontWeight="bold">{value}</Text>
                        </Box>
                    ))}
                </Grid>

                {loading && <Flex justify="center" py={8}><Spinner color="green.500" /></Flex>}
                {error && <Text color="red.600">Unable to load driver history: {error}</Text>}
                {!loading && !error && history.length === 0 && (
                    <Text color="gray.600">Historical driver data is unavailable for this company.</Text>
                )}
                {history.length > 0 && (
                    <>
                    <Box overflowX="auto">
                        <Table.Root size="sm" minW="720px" variant="line">
                            <Table.Header>
                                <Table.Row>
                                    <Table.ColumnHeader>Driver</Table.ColumnHeader>
                                    {history.map((entry) => (
                                        <Table.ColumnHeader key={entry.year} textAlign="right">{entry.year}</Table.ColumnHeader>
                                    ))}
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {[
                                    ['Revenue', 'revenue', { currency: true }],
                                    ['Revenue Growth', 'revenue_growth', { percent: true }],
                                    ['Operating Margin', 'operating_margin', { percent: true }],
                                    ['Free Cash Flow', 'fcf', { currency: true }],
                                    ['FCF Margin', 'fcf_margin', { percent: true }],
                                ].map(([label, key, options]) => (
                                    <Table.Row key={key}>
                                        <Table.Cell fontWeight="semibold" whiteSpace="nowrap">{label}</Table.Cell>
                                        {history.map((entry) => (
                                            <Table.Cell key={entry.year} textAlign="right">
                                                {formatCompact(entry[key], options)}
                                            </Table.Cell>
                                        ))}
                                    </Table.Row>
                                ))}
                            </Table.Body>
                        </Table.Root>
                    </Box>
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} mt={4}>
                        <Box bg="gray.50" borderRadius="lg" p={3}>
                            <Text fontWeight="semibold">Revenue Growth</Text>
                            <AverageButtons driver="revenue_growth" history={history} dispatch={dispatchDcf} />
                        </Box>
                        <Box bg="gray.50" borderRadius="lg" p={3}>
                            <Text fontWeight="semibold">Operating Margin</Text>
                            <AverageButtons driver="operating_margin" history={history} dispatch={dispatchDcf} />
                        </Box>
                    </Grid>
                    </>
                )}
            </SectionCard>

            <SectionCard id="valuation" title="Valuation" description="Bridge enterprise value to equity value and intrinsic value per share.">
                <ValuationBridge
                    result={dcfResult}
                    loading={isDcfLoading}
                    error={dcfError}
                    currentPrice={price?.value}
                    sharesOutstanding={dcfState.sharesOutstanding}
                    dispatch={dispatchDcf}
                />
            </SectionCard>

            <SectionCard id="scenarios" title="Scenarios" description="Save, load, and compare sets of DCF assumptions.">
                <DcfScenarios
                    ticker={normalizedTicker}
                    assumptions={dcfState}
                    dispatch={dispatchDcf}
                    currentPrice={price?.value}
                    isAuthenticated={isAuthenticated}
                    getAccessTokenSilently={getAccessTokenSilently}
                    loginWithRedirect={loginWithRedirect}
                    calculationLoading={isDcfLoading}
                    calculationError={dcfError}
                />
            </SectionCard>

            <Box as="section" id="ai-rating">
                <Accordion.Root collapsible defaultValue={['ai-rating']} borderWidth="1px" borderRadius="xl" bg="white">
                    <Accordion.Item value="ai-rating" border="0">
                        <Accordion.ItemTrigger p={{ base: 4, md: 6 }}>
                            <Box flex="1" textAlign="left">
                                <Heading as="h2" size="lg">AI Rating</Heading>
                                <Text color="gray.600" mt={1}>Generate the existing teacher-grade qualitative rating.</Text>
                            </Box>
                            <Accordion.ItemIndicator />
                        </Accordion.ItemTrigger>
                        <Accordion.ItemContent>
                            <Accordion.ItemBody px={{ base: 4, md: 6 }} pb={{ base: 4, md: 6 }}>
                        <StockAnalysisRatingPanel
                            normalizedTicker={normalizedTicker}
                            authState={{ authenticated: isAuthenticated }}
                            usageState={{
                                data: usage,
                                loading: isUsageLoading,
                                error: usageError,
                                label: usageLabel,
                                usedRatings,
                                monthlyLimit,
                                premiumMember: isPremiumMember,
                                renewalDate,
                                canGenerate: canGenerateRating,
                            }}
                            ratingState={{
                                result: ratingResult,
                                loading: isRatingLoading,
                                error: ratingError,
                                buttonDisabled: ratingButtonDisabled,
                            }}
                            loadingState={{
                                messageIndex: loadingMessageIndex,
                                progress: loadingProgress,
                                messages: loadingMessages,
                            }}
                            onGenerateRating={handleGenerateRating}
                        />
                            </Accordion.ItemBody>
                        </Accordion.ItemContent>
                    </Accordion.Item>
                </Accordion.Root>
            </Box>
        </Flex>
    );
}

export default StockAnalysis;
